// 📄 pages/Soporte/TicketsPorAtender.tsx
// Bandeja del equipo consultor (T5 de docs/plans/plan-tickets-proyecto.md):
// los tickets activos de la Mesa de ayuda de TODOS sus proyectos, para no
// entrar proyecto por proyecto. Rol Soporte, PM y creador del proyecto ven los
// sin asignar y los suyos; el resto, los que tiene asignados. La lista la arma
// el backend; cada fila abre el ticket dentro de su proyecto.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { BandejaTicket, BandejaTickets, ESTADO_TICKET_BADGE, PRIORIDAD_TICKET_COLOR, obtenerBandejaTickets } from '../../services/mesaAyudaService.ts';

type Filtro = 'todos' | 'sinAsignar' | 'mios' | 'respondidos';

const FILTROS: { id: Filtro; label: string; cumple: (t: BandejaTicket) => boolean }[] = [
  { id: 'todos', label: 'Todos', cumple: () => true },
  { id: 'respondidos', label: 'El cliente respondió', cumple: (t) => t.clientReplied },
  { id: 'sinAsignar', label: 'Sin asignar', cumple: (t) => t.unassigned },
  { id: 'mios', label: 'Asignados a mí', cumple: (t) => t.assignedToMe }
];

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '');

export default function TicketsPorAtender() {
  const [bandeja, setBandeja] = useState<BandejaTickets | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todos');

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      setBandeja(await obtenerBandejaTickets());
    } catch (err: any) {
      setError(err.message || 'No se pudo cargar la bandeja.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const visibles = useMemo(() => {
    const { cumple } = FILTROS.find((f) => f.id === filtro) || FILTROS[0];
    return (bandeja?.tickets || []).filter(cumple);
  }, [bandeja, filtro]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <Inbox className="w-5 h-5 text-blue-400" /> Tickets por atender
        </h1>
        <button
          onClick={cargar}
          disabled={cargando}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white disabled:opacity-50 border border-white/10 rounded-lg px-3 py-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <p className="text-sm text-gray-400">
        Tickets activos de la Mesa de ayuda de tus proyectos. Primero los que esperan respuesta y los de mayor prioridad.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              filtro === f.id ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
            }`}
          >
            {f.label} ({(bandeja?.tickets || []).filter(f.cumple).length})
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}
      {bandeja === null && !error ? (
        <div className="p-8 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /></div>
      ) : visibles.length === 0 && !error ? (
        <p className="text-sm text-gray-500">{bandeja?.tickets.length ? 'Ningún ticket con este filtro.' : 'No tienes tickets por atender.'}</p>
      ) : (
        <div className="space-y-2">
          {visibles.map((t) => (
            <Link
              key={`${t.projectId}/${t.id}`}
              to={`/proyecto/${t.projectId}/mesa-ayuda?ticket=${t.id}`}
              className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 space-y-1.5 transition-all"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-gray-500">{t.number}</span>
                <span className="font-medium text-white">{t.title}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ESTADO_TICKET_BADGE[t.status]}`}>{t.status}</span>
                <span className={`text-[11px] ${PRIORIDAD_TICKET_COLOR[t.priority]}`}>{t.priority}</span>
                {t.clientReplied && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20">El cliente respondió</span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {t.projectName}{t.clientName ? ` · ${t.clientName}` : ''} · {t.createdByName} · {fecha(t.createdAt)} ·{' '}
                {t.unassigned ? <span className="text-amber-300">Sin asignar</span> : t.assignedToMe ? 'Asignado a mí' : `Asignado a ${t.assigneeName}`}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** Tarjeta de Inicio: solo aparece si hay algo por atender. */
export function TicketsPorAtenderTarjeta() {
  const { user } = useAuth();
  const [bandeja, setBandeja] = useState<BandejaTickets | null>(null);

  // Inicio también se ve sin sesión, y la sesión puede llegar después de montar.
  useEffect(() => {
    if (!user) return;
    obtenerBandejaTickets().then(setBandeja).catch((err) => console.error('Error cargando la bandeja de tickets:', err));
  }, [user]);

  if (!bandeja?.tickets.length) return null;
  const { total, unassigned, clientReplied } = bandeja.summary;
  const detalle = [unassigned ? `${unassigned} sin asignar` : '', clientReplied ? `${clientReplied} con respuesta del cliente` : ''].filter(Boolean).join(' · ');

  return (
    <Link
      to="/tickets-por-atender"
      className="w-full max-w-4xl mt-6 flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-left transition-all"
    >
      <Inbox className="w-5 h-5 text-blue-400 flex-shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-white">{total === 1 ? '1 ticket por atender' : `${total} tickets por atender`}</span>
        {detalle && <span className="block text-xs text-gray-500">{detalle}</span>}
      </span>
    </Link>
  );
}
